<template>
	<div class="blog-outer-wrapper">
		<div class="blog-inner-wrapper">
			<h1>Blog</h1>
			<p class="blog-subtitle">
				Insights and articles from the ENON Solutions team.
			</p>
		</div>
	</div>

	<div class="blog-list-outer-wrapper">
		<div class="blog-list-inner-wrapper">
			<div v-if="loading" class="blog-list__state">Loading...</div>

			<div v-else-if="error" class="blog-list__state blog-list__state--error">
				Failed to load articles. Please try again later.
			</div>

			<template v-else>
				<div class="blog-list__grid">
					<router-link
						v-for="blog in blogs"
						:key="blog.link"
						:to="{ name: 'BlogPost', params: { link: blog.link } }"
						class="blog-list__link"
					>
						<blog-card :data="blog" />
					</router-link>
				</div>

				<div v-if="totalPages > 1" class="blog-pagination">
					<button
						class="blog-pagination__btn"
						:disabled="currentPage === 1"
						@click="goToPage(currentPage - 1)"
					>
						&#8592;
					</button>

					<button
						v-for="page in totalPages"
						:key="page"
						class="blog-pagination__btn"
						:class="{ 'blog-pagination__btn--active': page === currentPage }"
						@click="goToPage(page)"
					>
						{{ page }}
					</button>

					<button
						class="blog-pagination__btn"
						:disabled="currentPage === totalPages"
						@click="goToPage(currentPage + 1)"
					>
						&#8594;
					</button>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup>
import api from "@/api/index.js";

const PER_PAGE = 6;

const blogs = ref([]);
const loading = ref(false);
const error = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);

async function fetchBlogs(page) {
	loading.value = true;
	error.value = false;
	try {
		const res = await api.getBlogs({ page, perPage: PER_PAGE });
		blogs.value = res.data.items || [];
		totalPages.value = res.data.totalPages ?? 1;
	} catch {
		error.value = true;
	} finally {
		loading.value = false;
	}
}

function goToPage(page) {
	if (page < 1 || page > totalPages.value) return;
	currentPage.value = page;
	window.scrollTo({ top: 0, behavior: "smooth" });
	fetchBlogs(page);
}

onMounted(() => fetchBlogs(1));
</script>

<style lang="scss" scoped>
.blog-outer-wrapper {
	background: #0d1015;
	padding: calc(#{$top-page-nav-height} + 48px) 20px 60px;
}

.blog-inner-wrapper {
	max-width: $content-max-width;
	margin: 0 auto;

	h1 {
		font-size: 48px;
		font-weight: 700;
		color: #fff;
		margin: 0 0 12px;
	}
}

.blog-subtitle {
	font-size: 18px;
	color: #8a9aaa;
	margin: 0;
}

.blog-list-outer-wrapper {
	padding: 60px 20px 80px;
}

.blog-list-inner-wrapper {
	max-width: $content-max-width;
	margin: 0 auto;
}

.blog-list__state {
	text-align: center;
	font-size: 16px;
	color: #5a6a78;
	padding: 60px 0;

	&--error {
		color: #c0392b;
	}
}

.blog-list__grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 28px;
}

.blog-list__link {
	text-decoration: none;
	color: inherit;
	display: block;
}

.blog-pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 56px;

	&__btn {
		min-width: 40px;
		height: 40px;
		padding: 0 12px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.1);
		background: color-mix(in oklab, #0d1015, white 10%);
		font-size: 15px;
		font-weight: 600;
		color: #fff;
		cursor: pointer;
		transition:
			background 0.15s,
			color 0.15s,
			border-color 0.15s;

		&:hover:not(:disabled) {
			background: $app-color;
			color: #fff;
			border-color: $app-color;
		}

		&--active {
			background: $app-color;
			color: #fff;
			border-color: $app-color;
		}

		&:disabled {
			opacity: 0.35;
			cursor: default;
		}
	}
}

@media screen and (width < 768px) {
	.blog-inner-wrapper h1 {
		font-size: 32px;
	}
}
</style>
